/**
 * KOL promo — landing popup + cart notice.
 *
 * A visitor arriving via a KOL story link (?utm_content matches a configured
 * entry) gets a creator-specific discount code:
 *  - the code is cached in localStorage (with an expiry) so it survives
 *    navigation and return visits;
 *  - Shopify's discount cookie is set in the background (/discount/CODE) so
 *    checkout applies the code automatically;
 *  - <kol-promo-popup> shows the code on the homepage after the cookie banner
 *    is answered, replacing the newsletter "10% off first order" popup;
 *  - <kol-promo-cart-notice> (rendered in the cart drawer and cart page)
 *    reminds the shopper the code will be applied at checkout.
 */

const isKolDesignMode = () =>
  (typeof Shopify !== 'undefined' && Shopify.designMode) ||
  document.documentElement.classList.contains('shopify-design-mode');

const KolPromoStore = {
  promoKey: 'theme:kol-promo',
  dismissedKey: 'theme:kol-promo-dismissed',
  appliedKey: 'theme:kol-promo-applied',
  memoryPromo: null,

  getMap() {
    if (this.map) return this.map;

    try {
      this.map = JSON.parse(document.querySelector('[data-kol-map]')?.textContent || '[]');
    } catch (_error) {
      this.map = [];
    }
    if (!Array.isArray(this.map)) this.map = [];

    return this.map;
  },

  ttlMs() {
    const days = parseFloat(document.querySelector('kol-promo-popup')?.dataset.promoTtlDays || '7');
    return (Number.isFinite(days) && days > 0 ? days : 7) * 24 * 60 * 60 * 1000;
  },

  findPromo(value) {
    for (const entry of this.getMap()) {
      if (!entry || !entry.code) continue;

      const matches = String(entry.match || '')
        .split(',')
        .map((candidate) => candidate.trim().toLowerCase())
        .filter(Boolean);

      if (matches.includes(value)) {
        return { code: String(entry.code).trim(), utm: value };
      }
    }
    return null;
  },

  /** Reads ?utm_content and, on a match, caches the promo for the session ahead. */
  capture() {
    let utmContent = null;
    try {
      utmContent = new URLSearchParams(window.location.search).get('utm_content');
    } catch (_error) {
      return;
    }
    if (!utmContent) return;

    const promo = this.findPromo(utmContent.trim().toLowerCase());
    if (!promo) return;

    this.memoryPromo = promo;

    try {
      localStorage.setItem(this.promoKey, JSON.stringify({ ...promo, expiresAt: Date.now() + this.ttlMs() }));
      // A fresh arrival through a story link always earns the popup again and
      // re-arms the discount cookie, even after an earlier dismissal.
      sessionStorage.removeItem(this.dismissedKey);
      sessionStorage.removeItem(this.appliedKey);
    } catch (_error) {
      /* Storage unavailable — the in-memory promo still covers this page view. */
    }
  },

  get() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.promoKey) || 'null');
      if (stored?.code) {
        if (!stored.expiresAt || stored.expiresAt > Date.now()) return stored;
        localStorage.removeItem(this.promoKey);
        return null;
      }
    } catch (_error) {
      /* fall through to the in-memory copy */
    }
    return this.memoryPromo;
  },

  hasDismissed() {
    try {
      return sessionStorage.getItem(this.dismissedKey) === 'true';
    } catch (_error) {
      return false;
    }
  },

  rememberDismissal() {
    try {
      sessionStorage.setItem(this.dismissedKey, 'true');
    } catch (_error) {
      /* ignore */
    }
  },

  /**
   * Sets Shopify's discount cookie in the background so checkout applies the
   * code even if the shopper never clicks the popup CTA. Re-armed once per
   * session (and again on every fresh UTM landing).
   */
  applyDiscountCookie() {
    const promo = this.get();
    if (!promo?.code) return;

    let applied = null;
    try {
      applied = sessionStorage.getItem(this.appliedKey);
    } catch (_error) {
      /* ignore — worst case we fetch once more */
    }
    if (applied === promo.code) return;

    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}discount/${encodeURIComponent(promo.code)}`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) return;

        let persisted = false;
        try {
          sessionStorage.setItem(this.appliedKey, promo.code);
          persisted = sessionStorage.getItem(this.appliedKey) === promo.code;
        } catch (_error) {
          /* ignore */
        }

        // Any cart HTML rendered before this fetch (drawer badges, totals)
        // may still show a previously applied code — re-render from server.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // The cart page itself is static HTML; reload it once (guarded by the
        // persisted flag so a storage-less browser can't reload-loop).
        if (persisted && window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
        }
      })
      .catch(() => {});
  },
};

/**
 * Account gate — when enabled in the section settings, a guest holding an
 * active KOL code is sent to sign in / create an account before checkout.
 * Uses a capture-phase listener so it runs ahead of the theme's own checkout
 * handlers (policy modal, terms validation) on every checkout entry point in
 * the cart drawer and cart page.
 */
const KolPromoAccountGate = {
  resumeKey: 'theme:kol-checkout-after-login',

  init() {
    const popupEl = document.querySelector('kol-promo-popup');
    if (!popupEl) return;

    this.loginUrl = popupEl.dataset.loginUrl || '/account/login';

    if (popupEl.dataset.customerLoggedIn === 'true') {
      this.resumeCheckout();
      return;
    }
    if (popupEl.dataset.requireAccount !== 'true') return;

    document.addEventListener('click', (event) => this.onClick(event), true);
  },

  isActive() {
    return !!KolPromoStore.get();
  },

  onClick(event) {
    if (!this.isActive()) return;

    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('#checkoutButton, #policyConfirmButton, [name="checkout"], a[href$="/checkout"]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    // Remember the checkout intent: the hosted login (new customer accounts)
    // ignores return params, so we resume checkout ourselves when the shopper
    // lands back on the storefront signed in (see resumeCheckout).
    try {
      sessionStorage.setItem(this.resumeKey, String(Date.now() + 30 * 60 * 1000));
    } catch (_error) {
      /* ignore */
    }

    const root = window.Shopify?.routes?.root || '/';
    const checkoutUrl = encodeURIComponent(root + 'checkout');
    const joiner = this.loginUrl.includes('?') ? '&' : '?';
    // return_url (classic accounts) + return_to (new accounts): whichever the
    // login flow honors, the shopper comes back ready for checkout; the
    // sessionStorage flag covers the case where both are ignored.
    window.location.href = `${this.loginUrl}${joiner}return_url=${checkoutUrl}&return_to=${checkoutUrl}`;
  },

  /** Signed in with a pending checkout intent → continue straight to checkout. */
  resumeCheckout() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(this.resumeKey);
    } catch (_error) {
      return;
    }
    if (!raw) return;

    try {
      sessionStorage.removeItem(this.resumeKey);
    } catch (_error) {
      /* ignore */
    }

    if (parseInt(raw, 10) > Date.now() && this.isActive()) {
      window.location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
    }
  },
};

/**
 * Terms dialog — clicking any discount badge in the cart (drawer or cart page)
 * opens the promo terms & conditions. Content is reused from the KOL popup
 * section's terms panel, so it stays editable in one place in the theme editor.
 */
const KolPromoTermsDialog = {
  ensure() {
    if (this.overlay) return this.overlay;

    const source = document.querySelector('[data-kol-terms-panel]');
    if (!source) return null;

    const overlay = document.createElement('div');
    overlay.className = 'kol-terms-dialog';
    overlay.hidden = true;

    const backdrop = document.createElement('div');
    backdrop.className = 'kol-terms-dialog__backdrop';
    backdrop.setAttribute('data-kol-terms-close', '');

    const card = document.createElement('div');
    card.className = 'kol-terms-dialog__card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('tabindex', '-1');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'kol-terms-dialog__close';
    close.setAttribute('data-kol-terms-close', '');
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';

    const title = document.createElement('p');
    title.className = 'kol-terms-dialog__title';
    title.textContent = document.querySelector('[data-kol-terms-toggle]')?.textContent?.trim()
      || 'Điều kiện & Điều khoản áp dụng';
    card.setAttribute('aria-label', title.textContent);

    const content = document.createElement('div');
    content.className = 'kol-terms-dialog__content';
    content.innerHTML = source.innerHTML;

    card.append(close, title, content);
    overlay.append(backdrop, card);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-kol-terms-close]')) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !overlay.hidden) {
        event.preventDefault();
        this.close();
      }
    });

    this.overlay = overlay;
    return overlay;
  },

  open() {
    const overlay = this.ensure();
    if (!overlay) return false;

    overlay.hidden = false;
    window.requestAnimationFrame(() => {
      overlay.querySelector('.kol-terms-dialog__close')?.focus({ preventScroll: true });
    });
    return true;
  },

  close() {
    if (this.overlay) this.overlay.hidden = true;
  },

  init() {
    if (!document.querySelector('[data-kol-terms-panel]')) return;

    // Signals CSS to render cart discount badges as clickable.
    document.documentElement.classList.add('kol-terms-available');

    document.addEventListener('click', (event) => {
      const badge = event.target instanceof Element ? event.target.closest('.discount-badge') : null;
      if (!badge) return;
      if (this.open()) event.preventDefault();
    });
  },
};

if (!isKolDesignMode()) {
  KolPromoStore.capture();
  KolPromoStore.applyDiscountCookie();
  KolPromoAccountGate.init();
  KolPromoTermsDialog.init();
}

class KolPromoPopup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.enabled = this.dataset.enabled === 'true';
    this.isIndex = this.dataset.isIndex === 'true';
    this.showOnlyOnIndex = this.dataset.showOnlyOnIndex === 'true';
    this.apparitionDelay = parseInt(this.dataset.apparitionDelay || '0', 10) * 1000;
    this.autoApplyCode = this.dataset.autoApplyCode === 'true';
    this.previouslyFocusedElement = null;
    this.addedPageLock = false;
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

    this.bindEvents();
    document.addEventListener('keydown', this.onDocumentKeydown);

    if (isKolDesignMode()) {
      this.bindEditorEvents();
      return;
    }
    if (!this.enabled) return;

    const promo = KolPromoStore.get();
    if (!promo) return;

    // The KOL offer replaces the newsletter popup while the promo is active,
    // even on pages where the KOL popup itself does not show.
    this.suppressNewsletterPopup();

    if (this.showOnlyOnIndex && !this.isIndex) return;
    if (KolPromoStore.hasDismissed()) return;

    this.applyPromo(promo);
    this.whenConsentSettled(() => {
      window.setTimeout(() => this.show(), this.apparitionDelay);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.newsletterObserver?.disconnect();
    this.unlockPage();
  }

  /* -------------------------------------------------------------------- */
  /* Content                                                               */
  /* -------------------------------------------------------------------- */

  applyPromo(promo) {
    this.promo = promo;

    this.querySelectorAll('[data-kol-code-text]').forEach((element) => {
      element.textContent = promo.code;
    });

    const cta = this.querySelector('[data-kol-cta]');
    if (!cta) return;

    const root = window.Shopify?.routes?.root || '/';
    if (!this.ctaTarget) this.ctaTarget = cta.getAttribute('href') || root;

    if (this.autoApplyCode) {
      // Shopify's /discount/CODE URL stores the code so checkout applies it
      // automatically; redirect brings the shopper back to the CTA target.
      cta.setAttribute('href', `${root}discount/${encodeURIComponent(promo.code)}?redirect=${encodeURIComponent(this.ctaTarget)}`);
    }
  }

  /* -------------------------------------------------------------------- */
  /* Cookie consent gating                                                 */
  /* -------------------------------------------------------------------- */

  whenConsentSettled(callback) {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      callback();
    };

    const gate = () => {
      // shouldShowBanner() can stay true on this store even after the visitor
      // already answered the banner on an earlier visit (the banner itself no
      // longer renders then). So only hold the popup while the native banner
      // is actually on screen — and never hold it indefinitely.
      if (!window.Shopify?.customerPrivacy?.shouldShowBanner?.()) return settle();

      document.addEventListener('visitorConsentCollected', settle, { once: true });

      let checks = 0;
      const poll = () => {
        if (settled) return;
        if (document.getElementById('shopify-pc__banner')) {
          // Banner is up: wait for the visitor's answer, capped so an ignored
          // banner cannot strand the promo (banner is bottom-anchored, the
          // popup is centered — they can coexist).
          window.setTimeout(settle, 20000);
        } else if (++checks >= 7) {
          settle(); // ~3.5s with no banner — consent was handled earlier
        } else {
          window.setTimeout(poll, 500);
        }
      };
      poll();
    };

    if (window.Shopify?.customerPrivacy) {
      gate();
    } else if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], gate);
    } else {
      gate();
    }
  }

  /* -------------------------------------------------------------------- */
  /* Newsletter popup replacement                                          */
  /* -------------------------------------------------------------------- */

  suppressNewsletterPopup() {
    const popup = document.querySelector('newsletter-popup');
    if (!popup) return;

    const suppress = () => {
      if (!popup.hasAttribute('open')) return;

      if (typeof popup.hide === 'function') {
        popup.hide();
      } else {
        popup.removeAttribute('open');
      }
      // Opening marks theme:popup-appeared even though the shopper never saw
      // it — un-spend the show-once flag so the newsletter popup can still
      // appear on a later, non-KOL visit. theme:popup-filled is preserved.
      try {
        localStorage.removeItem('theme:popup-appeared');
      } catch (_error) {
        /* ignore */
      }
    };

    suppress();
    this.newsletterObserver = new MutationObserver(suppress);
    this.newsletterObserver.observe(popup, { attributes: true, attributeFilter: ['open'] });
  }

  /* -------------------------------------------------------------------- */
  /* Interaction                                                           */
  /* -------------------------------------------------------------------- */

  bindEvents() {
    this.querySelectorAll('[data-action="dismiss"]').forEach((element) => {
      element.addEventListener('click', () => this.hide());
    });

    this.querySelector('[data-kol-cta]')?.addEventListener('click', () => {
      if (!isKolDesignMode()) KolPromoStore.rememberDismissal();
    });

    this.querySelector('[data-kol-copy]')?.addEventListener('click', () => this.copyCode());

    const termsToggle = this.querySelector('[data-kol-terms-toggle]');
    const termsPanel = this.querySelector('[data-kol-terms-panel]');
    if (termsToggle && termsPanel) {
      termsToggle.addEventListener('click', () => {
        const expanded = termsToggle.getAttribute('aria-expanded') === 'true';
        termsToggle.setAttribute('aria-expanded', String(!expanded));
        termsPanel.hidden = expanded;
      });
    }
  }

  copyCode() {
    const code = this.promo?.code || this.querySelector('[data-kol-code-text]')?.textContent?.trim();
    if (!code || !navigator.clipboard?.writeText) return;

    navigator.clipboard
      .writeText(code)
      .then(() => {
        this.classList.add('is-copied');
        window.clearTimeout(this.copiedTimeout);
        this.copiedTimeout = window.setTimeout(() => this.classList.remove('is-copied'), 2000);
      })
      .catch(() => {});
  }

  getFocusableElements() {
    return Array.from(this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.getClientRects().length > 0);
  }

  onDocumentKeydown(event) {
    if (!this.classList.contains('is-visible')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    const card = this.querySelector('.kol-promo__card');

    if (focusableElements.length === 0) {
      event.preventDefault();
      card?.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    } else if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === card)) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  }

  /* -------------------------------------------------------------------- */
  /* Visibility                                                            */
  /* -------------------------------------------------------------------- */

  show() {
    if (this.classList.contains('is-visible')) return;

    this.previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');
    this.lockPage();

    window.requestAnimationFrame(() => {
      this.querySelector('.kol-promo__close')?.focus({ preventScroll: true });
    });
  }

  hide() {
    if (!this.classList.contains('is-visible')) return;

    this.classList.remove('is-visible');
    this.setAttribute('aria-hidden', 'true');
    this.unlockPage();
    if (!isKolDesignMode()) KolPromoStore.rememberDismissal();

    if (this.previouslyFocusedElement?.isConnected) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    }
    this.previouslyFocusedElement = null;
  }

  lockPage() {
    this.addedPageLock = !document.documentElement.classList.contains('lock');
    document.documentElement.classList.add('lock');
  }

  unlockPage() {
    if (!this.addedPageLock) return;

    if (!document.querySelector('[open][aria-modal="true"]')) {
      document.documentElement.classList.remove('lock');
    }
    this.addedPageLock = false;
  }

  /* -------------------------------------------------------------------- */
  /* Theme editor preview                                                  */
  /* -------------------------------------------------------------------- */

  bindEditorEvents() {
    const section = this.closest('.shopify-section');

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target !== section) return;
      this.applyPromo(this.getPreviewPromo());
      this.show();
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target === section) this.hide();
    });
  }

  getPreviewPromo() {
    const first = KolPromoStore.getMap().find((entry) => entry?.code);
    return { code: first ? String(first.code).trim() : 'KOLCODE10', utm: 'preview' };
  }
}

if (!window.customElements.get('kol-promo-popup')) {
  window.customElements.define('kol-promo-popup', KolPromoPopup);
}

/**
 * Cart notice — rendered by snippets/kol-promo-cart-notice.liquid inside the
 * cart drawer and cart page. Re-created (and thus re-populated) every time
 * the cart re-renders. Stays hidden when no promo is cached or the code is
 * already applied to the cart server-side.
 */
class KolPromoCartNotice extends HTMLElement {
  connectedCallback() {
    if (isKolDesignMode()) {
      this.hidden = true;
      return;
    }

    const promo = KolPromoStore.get();
    if (!promo?.code) {
      this.hidden = true;
      return;
    }

    const appliedCodes = (this.dataset.appliedCodes || '')
      .split(',')
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean);
    if (appliedCodes.includes(promo.code.toLowerCase())) {
      this.hidden = true;
      return;
    }

    const target = this.querySelector('[data-kol-notice-text]');
    if (!target) return;

    const template = this.dataset.template || 'Code [[code]] will be applied automatically at checkout.';
    target.textContent = '';
    template.split('[[code]]').forEach((part, index) => {
      if (index > 0) {
        const strong = document.createElement('strong');
        strong.className = 'kol-promo-cart-notice__code';
        strong.textContent = promo.code;
        target.appendChild(strong);
      }
      if (part) target.appendChild(document.createTextNode(part));
    });

    // Sign-in hint: shown to guests when the account gate is on, so the
    // checkout redirect to login never comes as a surprise.
    const signin = this.querySelector('[data-kol-signin]');
    if (signin) {
      const popupEl = document.querySelector('kol-promo-popup');
      signin.hidden = !(popupEl?.dataset.requireAccount === 'true' && popupEl?.dataset.customerLoggedIn !== 'true');
    }

    this.hidden = false;
  }
}

if (!window.customElements.get('kol-promo-cart-notice')) {
  window.customElements.define('kol-promo-cart-notice', KolPromoCartNotice);
}
